import React, { Fragment, useContext, useMemo } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import { AppContext } from "../context/AppContext";
import Utils from "../utils/Utils";
import { useNavigate } from "react-router-dom";
import * as Fi from "react-icons/fi";
import { HashLoader } from "react-spinners";
import { MdKeyboardArrowLeft } from "react-icons/md";
import "../styles/style.css";

function toneFor(value) {
  const v = String(value ?? "").toLowerCase();
  if (
    ["enabled", "active", "yes", "any time", "form view", "calendar view"].some(
      (x) => v === x || v.startsWith(x)
    )
  ) {
    return "on";
  }
  if (["disabled", "inactive", "no"].includes(v)) return "off";
  return "neutral";
}

function ReservationPreferences() {
  const navigate = useNavigate();
  const { reservSettingsLoading, foodpageReservSettings: s } =
    useContext(AppContext);

  const groups = useMemo(() => {
    if (!s) return [];

    const early =
      s.earlyBooking === "any_time" || s.earlyBooking == 0
        ? "Any Time"
        : `From ${s.earlyBooking ?? 0} day(s) in advance`;

    const late =
      Number(s.lateBooking) > 45
        ? `From ${Utils.convertMinutesToHours(s.lateBooking ?? 0)} hour(s) in advance`
        : `From ${s.lateBooking ?? 0} minute(s) in advance`;

    const lateCancel =
      String(s.lateCancel) === "1"
        ? "Up to last minute"
        : Number(s.lateCancel) > 45
          ? `At least ${Utils.convertMinutesToHours(s.lateCancel ?? 0)} hour(s) in advance`
          : `At least ${s.lateCancel ?? 0} minute(s) in advance`;

    const advance =
      s.haveAdvance === "Yes"
        ? `${s?.currencyDetails?.currencySymbol ?? ""}${s?.advanceAmount ?? "0"} (${
            s.advanceType === "Chair"
              ? "Per Chair"
              : s.advanceType === "Table"
                ? "Per Table"
                : "Total"
          })`
        : "No";

    const autoApprove =
      s.autoApprove === "Enabled" && s.autoApproveChairLimit
        ? `Enabled (≤ ${s.autoApproveChairLimit} chairs)`
        : (s.autoApprove ?? "Disabled");

    const booking = [
      {
        label: "Maximum Party Size",
        hint: "Largest party size guests can book.",
        value: String(s.maxPartySize ?? 0),
      },
      {
        label: "Early Booking",
        hint: "How far ahead guests can reserve.",
        value: early,
      },
      {
        label: "Late Booking",
        hint: "Latest cut-off before the booking time.",
        value: late,
      },
      {
        label: "Late Cancel",
        hint: "How late guests can cancel.",
        value: lateCancel,
      },
      {
        label: "Time Interval",
        hint: "Gap between available time slots.",
        value: `Every ${s.timeinterval || 0} minutes`,
      },
      {
        label: "View type",
        hint: "How the booking page is shown.",
        value: s.viewType == "formView" ? "Form View" : "Calendar View",
      },
    ];

    const approvals = [
      {
        label: "Auto Approve",
        hint: "Automatically approve matching requests.",
        value: autoApprove,
      },
      {
        label: "Advance Payment",
        hint: "Whether an advance amount is required.",
        value: advance,
      },
    ];

    if (s.haveAdvance === "Yes" && s.advanceType === "Chair") {
      approvals.push({
        label: "Min Chairs for Advance",
        hint: "Advance applies from this party size.",
        value: String(s.minChairs ?? 0),
      });
    }

    approvals.push({
      label: "OTP Verify",
      hint: "Require OTP verification for bookings.",
      value: s.otpVerify ?? "Inactive",
    });

    const guests = [
      {
        label: "Guest Reservations",
        hint: "Allow guests without an account to book.",
        value: s.reservationGuestAllow ?? "Disabled",
      },
      {
        label: "Guest OTP Verification",
        hint: "Require OTP for guest bookings.",
        value: s.reservationGuestOTPVerification ?? "Disabled",
      },
      {
        label: "Monthly Report",
        hint: "Automated monthly reservation report email.",
        value:
          s.automatedMonthlyReport === "Yes"
            ? s.monthlyReportEmail || "Yes"
            : "No",
      },
    ];

    return [
      { title: "Booking rules", items: booking },
      { title: "Approvals & payments", items: approvals },
      { title: "Guests & reports", items: guests },
    ];
  }, [s]);

  return (
    <Fragment>
      <MasterLayout>
        <div className="rs-settings">
          <header className="rs-settings__header">
            <button
              type="button"
              className="rs-settings__back"
              onClick={() => navigate("/reservation-settings")}
            >
              <MdKeyboardArrowLeft /> More
            </button>
            <div className="rs-settings__heading">
              <div className="rs-settings__heading-text">
                <h1 className="rs-settings__title">Reservation preferences</h1>
                <p className="rs-settings__subtitle">
                  Review booking rules, approvals, and guest options.
                </p>
              </div>
              <button
                type="button"
                className="rs-settings__edit"
                onClick={() =>
                  navigate("/edit-reservation-settings?section=preferences")
                }
              >
                <Fi.FiEdit3 /> Edit
              </button>
            </div>
          </header>

          {reservSettingsLoading ? (
            <div className="rs-settings__loader">
              <HashLoader color="#f44336" />
            </div>
          ) : groups.length > 0 ? (
            <div className="rs-pref-groups">
              {groups.map((group) => (
                <section key={group.title} className="rs-settings-card">
                  <h2 className="rs-settings-card__title">{group.title}</h2>
                  <ul className="rs-settings-extra">
                    {group.items.map((item) => (
                      <li key={item.label} className="rs-settings-extra__row">
                        <div className="rs-settings-extra__copy">
                          <div className="rs-settings-extra__label">
                            {item.label}
                          </div>
                          <div className="rs-settings-extra__hint">
                            {item.hint}
                          </div>
                        </div>
                        <div
                          className={`rs-settings-chip rs-settings-chip--${toneFor(
                            item.value
                          )}`}
                        >
                          {item.value}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ) : (
            <section className="rs-settings-card rs-settings-empty-card">
              <Fi.FiSliders className="rs-settings-empty-card__icon" />
              <p className="rs-settings-empty-card__title">
                Settings not available
              </p>
              <p className="rs-settings-empty-card__text">
                Preferences will appear here once reservation settings are
                loaded.
              </p>
            </section>
          )}
        </div>
      </MasterLayout>
    </Fragment>
  );
}

export default ReservationPreferences;
